import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, Loader2, AlertCircle, Sparkles, History, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { CreditCardVisual } from '@/components/cards/CreditCardVisual';
import { AdminAPI } from '@/lib/admin-api';
import { formatINR } from '@/lib/format';
import { formatDistanceToNow, formatDateTime } from '@/lib/format-time';
import { toast } from 'sonner';
import { CapabilityToggles, DEFAULT_CARD_CAPABILITIES } from '@/components/admin/CapabilityToggles';
import { EntityPicker } from '@/components/admin/EntityPicker';
import { CardAssetPanel } from '@/components/admin/CardAssetPanel';
import { AdminAPI as AdminAPIExt } from '@/lib/admin-api-ext';

const NETWORKS = ['VISA', 'MASTERCARD', 'AMEX', 'RUPAY', 'DINERS'];
const TYPES = ['rewards', 'cashback', 'miles'];
const CATEGORIES = ['travel', 'cashback', 'rewards', 'premium', 'lifestyle', 'co-brand'];
const STATUSES = ['active', 'draft', 'inactive'];
const DESIGNS = ['graphite-gold', 'navy-stars', 'silver-metallic', 'modern-teal', 'emerald-luxury'];

// ------------------------------------------------------------------
// ListTextField — controlled text field whose underlying value is an
// ARRAY of strings, separated by either `, ` (commas) or `\n` (newlines).
//
// Why this exists: the previous implementation re-derived the displayed
// text from the array on every keystroke and ran `.trim().filter(Boolean)`
// per keystroke. That stripped trailing/in-word spaces (you couldn't type
// "Dining Out" because the space got eaten before the next character
// arrived). This component keeps a LOCAL draft string for free typing
// and only normalises (trim + filter empties) on blur. The parent always
// receives the parsed array.
// ------------------------------------------------------------------
const ListTextField = ({ value, onChange, separator = ', ', multiline = false, ...rest }) => {
  const sep = separator;
  const splitRe = sep === '\n' ? /\n/ : /,/;
  const joiner = sep;
  const toText = (arr) => (Array.isArray(arr) ? arr.join(joiner) : '');
  const [draft, setDraft] = useState(toText(value));

  // Sync when the parent-managed array changes for an EXTERNAL reason
  // (e.g. card loaded from server, or a different tab modified it). We
  // only re-seed if the normalised draft would yield a different array
  // than what's coming in — otherwise the user's typing wins.
  useEffect(() => {
    const incoming = toText(value);
    const currentParsed = draft
      .split(splitRe)
      .map((s) => s.trim())
      .filter(Boolean);
    const incomingParsed = (value || []).slice();
    const same =
      currentParsed.length === incomingParsed.length &&
      currentParsed.every((v, i) => v === incomingParsed[i]);
    if (!same) setDraft(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (txt, { normalize }) => {
    let parts = txt.split(splitRe);
    if (normalize) parts = parts.map((s) => s.trim()).filter(Boolean);
    onChange(parts);
  };

  const Field = multiline ? Textarea : Input;
  return (
    <Field
      {...rest}
      value={draft}
      onChange={(e) => {
        const v = e.target.value;
        setDraft(v);
        // Emit the array WITHOUT trimming so the live state stays useful,
        // but skip empty fragments (they're noise) — trimming itself is
        // deferred to blur to preserve in-progress whitespace.
        emit(v, { normalize: false });
      }}
      onBlur={(e) => {
        const v = e.target.value;
        const cleaned = v
          .split(splitRe)
          .map((s) => s.trim())
          .filter(Boolean);
        // Re-format the visible text to canonical form on blur so the
        // user sees the normalised output (no trailing commas / spaces).
        setDraft(cleaned.join(joiner));
        onChange(cleaned);
      }}
    />
  );
};

const empty = (banks) => ({
  bank: banks[0]?.name || '', bank_id: banks[0]?.id || null,
  name: 'New Card', tier: 'Rewards', network: 'VISA', type: 'rewards', design: 'graphite-gold',
  category: 'rewards', last4: '0000', annual_fee: 0, joining_fee: 0, fee_waiver_inr: 0,
  fee_waiver_logic: '', welcome_bonus: '', currency: 'Reward Points', point_value_inr: 1,
  reward_expiry_months: null, fuel_surcharge_waiver: false, lounge_access: null,
  transfer_partners: [], best_for: [], benefits: [], status: 'draft',
  capabilities: { ...DEFAULT_CARD_CAPABILITIES },
});

export default function AdminCardEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';   // no :id param OR explicit "new" => create mode
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [banks, setBanks] = useState([]);
  const [card, setCard] = useState(null);
  const [audits, setAudits] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const b = await AdminAPI.banks.list({ includeInactive: true });
        if (cancelled) return;
        setBanks(b || []);
        if (isNew) {
          // CREATE mode: do NOT call /api/admin/cards/{id}. Initialise empty form only.
          setCard(empty(b || []));
        } else {
          // EDIT mode: id is guaranteed defined and non-"new" here.
          const c = await AdminAPI.cards.get(id);
          if (cancelled) return;
          setCard(c);
          // Recent changes for this card \u2014 best-effort, never blocks the editor
          AdminAPI.auditLogs({ entity_type: 'card', entity_id: id, limit: 5 })
            .then((logs) => { if (!cancelled) setAudits(logs); })
            .catch(() => {});
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Load failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew]);

  const set = (patch) => setCard((c) => ({ ...c, ...patch }));

  const validations = useMemo(() => {
    const out = [];
    if (!card) return out;
    if (!card.name || card.name.trim().length < 2) out.push('Card name required');
    if (!card.bank) out.push('Bank required');
    if (!card.network) out.push('Network required');
    if (card.point_value_inr == null || isNaN(Number(card.point_value_inr))) out.push('Point value must be a number');
    if (card.last4 && !/^\d{4}$/.test(card.last4)) out.push('Last4 must be 4 digits');
    return out;
  }, [card]);

  const save = async () => {
    if (!card) return;
    if (validations.length) {
      toast.error(validations[0]);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...card,
        last4: card.last4 || '0000',
        point_value_inr: Number(card.point_value_inr) || 1,
        annual_fee: Number(card.annual_fee) || 0,
        joining_fee: Number(card.joining_fee) || 0,
        fee_waiver_inr: Number(card.fee_waiver_inr) || 0,
        reward_expiry_months: card.reward_expiry_months === '' || card.reward_expiry_months == null ? null : Number(card.reward_expiry_months),
      };
      if (isNew) {
        const created = await AdminAPI.cards.create(payload);
        toast.success(`Card “${created.name}” created. Edit rules next.`);
        navigate(`/admin/cards/${created.id}`);
      } else {
        await AdminAPI.cards.update(card.id, payload);
        toast.success('Card updated');
      }
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!card?.id || isNew) return;
    try {
      await AdminAPI.cards.remove(card.id);
      toast.success('Card archived');
      navigate('/admin/cards');
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    } finally {
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-64" />
        <div className="grid lg:grid-cols-[minmax(0,1fr)_420px] gap-4">
          <Skeleton className="h-[500px] w-full" />
          <Skeleton className="h-[500px] w-full" />
        </div>
      </div>
    );
  }
  if (error || !card) {
    return (
      <Card className="p-6 border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-300" data-testid="admin-error-alert">
        <AlertCircle className="h-5 w-5 inline mr-2" />{error || 'Card not found'}
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-card-editor">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3">
        <div className="min-w-0">
          <Button asChild size="sm" variant="ghost" className="-ml-2 gap-1.5 text-muted-foreground" data-testid="admin-card-back">
            <Link to="/admin/cards"><ChevronLeft className="h-3.5 w-3.5" /> Cards</Link>
          </Button>
          <h1 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-1">
            {isNew ? 'New card' : `${card.bank} — ${card.name}`}
          </h1>
          {!isNew && (
            <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
              <span className="font-mono-num">{card.id}</span>
              <span>·</span>
              <span>{card.active_rule_version_id ? `Active version: ${card.active_rule_version_id}` : 'No active version'}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <>
              <Button asChild size="sm" variant="outline" className="gap-1.5" data-testid="admin-card-go-rules">
                <Link to={`/admin/cards/${card.id}/rules`}><Sparkles className="h-3.5 w-3.5" /> Edit rules</Link>
              </Button>
              <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-rose-500" onClick={() => setConfirmDelete(true)} data-testid="admin-card-delete">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          <Button size="sm" onClick={save} disabled={saving} className="gap-1.5" data-testid="admin-card-editor-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {isNew ? 'Create card' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_420px] gap-4">
        {/* LEFT: form */}
        <Card className="p-4">
          <Tabs defaultValue="basics">
            {/* Mobile: horizontally scrollable tablist so all tabs remain reachable
                even at <420px without wrapping or being clipped. */}
            <div className="-mx-1 overflow-x-auto scrollbar-thin">
              <TabsList className="w-max justify-start flex-nowrap">
                <TabsTrigger value="basics" data-testid="admin-card-tab-basics">Basics</TabsTrigger>
                <TabsTrigger value="capabilities" data-testid="admin-card-tab-capabilities">Capabilities</TabsTrigger>
                <TabsTrigger value="fees" data-testid="admin-card-tab-fees">Fees & rewards</TabsTrigger>
                <TabsTrigger value="benefits" data-testid="admin-card-tab-benefits">Benefits</TabsTrigger>
                <TabsTrigger value="image" data-testid="admin-card-tab-image">Image</TabsTrigger>
                {card.capabilities?.supports_transfer_partners && (
                  <TabsTrigger value="transfer" data-testid="admin-card-tab-transfer">Transfer</TabsTrigger>
                )}
              </TabsList>
            </div>

            <TabsContent value="basics" className="mt-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Bank <span className="text-rose-500">*</span></Label>
                  <Select value={card.bank_id || ''} onValueChange={(v) => { const b = banks.find((x) => x.id === v); set({ bank_id: v, bank: b?.name || card.bank }); }}>
                    <SelectTrigger className="mt-1 h-9" data-testid="admin-card-form-bank"><SelectValue placeholder="Select bank" /></SelectTrigger>
                    <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Card name <span className="text-rose-500">*</span></Label>
                  <Input value={card.name} onChange={(e) => set({ name: e.target.value })} className="mt-1 h-9" data-testid="admin-card-form-name" />
                </div>
                <div>
                  <Label className="text-xs">Tier</Label>
                  <Input value={card.tier || ''} onChange={(e) => set({ tier: e.target.value })} placeholder="Metal / Premium / Co-brand" className="mt-1 h-9" data-testid="admin-card-form-tier" />
                </div>
                <div>
                  <Label className="text-xs">Network</Label>
                  <Select value={card.network} onValueChange={(v) => set({ network: v })}>
                    <SelectTrigger className="mt-1 h-9" data-testid="admin-card-form-network"><SelectValue /></SelectTrigger>
                    <SelectContent>{NETWORKS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={card.type} onValueChange={(v) => set({ type: v })}>
                    <SelectTrigger className="mt-1 h-9" data-testid="admin-card-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Category</Label>
                  <Select value={card.category || 'rewards'} onValueChange={(v) => set({ category: v })}>
                    <SelectTrigger className="mt-1 h-9" data-testid="admin-card-form-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Design</Label>
                  <Select value={card.design} onValueChange={(v) => set({ design: v })}>
                    <SelectTrigger className="mt-1 h-9" data-testid="admin-card-form-design"><SelectValue /></SelectTrigger>
                    <SelectContent>{DESIGNS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Last 4 (placeholder)</Label>
                  <Input value={card.last4 || ''} onChange={(e) => set({ last4: e.target.value.replace(/[^0-9]/g, '').slice(0, 4) })} maxLength={4} className="mt-1 h-9 font-mono-num" data-testid="admin-card-form-last4" />
                </div>
              </div>
              <Separator />
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={card.status || 'active'} onValueChange={(v) => set({ status: v })}>
                    <SelectTrigger className="mt-1 h-9" data-testid="admin-card-form-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border bg-[hsl(var(--surface-2))] px-3 py-2.5">
                  <div>
                    <Label className="text-sm font-medium">Fuel surcharge waiver</Label>
                    <div className="text-[10px] text-muted-foreground">Common 1% surcharge waiver at petrol pumps.</div>
                  </div>
                  <Switch checked={!!card.fuel_surcharge_waiver} onCheckedChange={(v) => set({ fuel_surcharge_waiver: v })} data-testid="admin-card-form-fuel-waiver" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="capabilities" className="mt-4 space-y-3">
              <div className="text-[11px] text-muted-foreground">
                Toggle the capabilities this card actually supports. The editor below will hide irrelevant sections automatically — keeping operators focused on the right fields per card archetype.
              </div>
              <CapabilityToggles
                value={card.capabilities || DEFAULT_CARD_CAPABILITIES}
                onChange={(v) => set({ capabilities: v })}
              />
            </TabsContent>

            <TabsContent value="fees" className="mt-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Annual fee (₹)</Label>
                  <Input type="number" value={card.annual_fee} onChange={(e) => set({ annual_fee: e.target.value })} className="mt-1 h-9 font-mono-num" data-testid="admin-card-form-annual-fee" />
                </div>
                <div>
                  <Label className="text-xs">Joining fee (₹)</Label>
                  <Input type="number" value={card.joining_fee} onChange={(e) => set({ joining_fee: e.target.value })} className="mt-1 h-9 font-mono-num" data-testid="admin-card-form-joining-fee" />
                </div>
                {card.capabilities?.has_fee_waiver && (
                  <div>
                    <Label className="text-xs">Fee waiver spend (₹)</Label>
                    <Input type="number" value={card.fee_waiver_inr} onChange={(e) => set({ fee_waiver_inr: e.target.value })} className="mt-1 h-9 font-mono-num" data-testid="admin-card-form-fee-waiver" />
                  </div>
                )}
                <div>
                  <Label className="text-xs">Reward expiry (months)</Label>
                  <Input type="number" value={card.reward_expiry_months ?? ''} onChange={(e) => set({ reward_expiry_months: e.target.value })} placeholder="e.g. 24" className="mt-1 h-9 font-mono-num" data-testid="admin-card-form-reward-expiry" />
                </div>
                <div>
                  <Label className="text-xs">Reward currency</Label>
                  <Input value={card.currency} onChange={(e) => set({ currency: e.target.value })} className="mt-1 h-9" data-testid="admin-card-form-currency" />
                </div>
                <div>
                  <Label className="text-xs">Point value (₹ per pt)</Label>
                  <Input type="number" step="0.01" value={card.point_value_inr} onChange={(e) => set({ point_value_inr: e.target.value })} className="mt-1 h-9 font-mono-num" data-testid="admin-card-form-point-value" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Fee waiver logic (free-text)</Label>
                <Textarea value={card.fee_waiver_logic || ''} onChange={(e) => set({ fee_waiver_logic: e.target.value })} rows={2} placeholder='e.g. "Waived if anniversary spend &gt;= ₹10L"' className="mt-1" data-testid="admin-card-form-fee-waiver-logic" disabled={!card.capabilities?.has_fee_waiver} />
              </div>
              <div>
                <Label className="text-xs">Welcome bonus (free-text)</Label>
                <Textarea value={card.welcome_bonus || ''} onChange={(e) => set({ welcome_bonus: e.target.value })} rows={2} placeholder='e.g. "12,500 RP on first spend"' className="mt-1" data-testid="admin-card-form-welcome" />
              </div>
            </TabsContent>

            <TabsContent value="benefits" className="mt-4 space-y-4">
              <div>
                <Label className="text-xs">Best for (comma separated)</Label>
                <ListTextField
                  value={card.best_for}
                  onChange={(arr) => set({ best_for: arr })}
                  separator=", "
                  placeholder="Dining, Travel, Insurance"
                  className="mt-1 h-9"
                  data-testid="admin-card-form-best-for"
                />
              </div>
              <div>
                <Label className="text-xs">Benefits (one per line)</Label>
                <ListTextField
                  multiline
                  rows={6}
                  value={card.benefits}
                  onChange={(arr) => set({ benefits: arr })}
                  separator={'\n'}
                  placeholder="Unlimited international lounges&#10;Golf privileges&#10;Concierge"
                  className="mt-1"
                  data-testid="admin-card-form-benefits"
                />
              </div>
              {card.capabilities?.has_lounge_access && (
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Lounge — domestic / quarter</Label>
                    <Input type="number" value={card.lounge_access?.domestic_per_quarter ?? ''} onChange={(e) => set({ lounge_access: { ...(card.lounge_access || {}), domestic_per_quarter: e.target.value === '' ? null : Number(e.target.value) } })} className="mt-1 h-9 font-mono-num" data-testid="admin-card-form-lounge-domestic" />
                  </div>
                  <div>
                    <Label className="text-xs">Lounge — international / year</Label>
                    <Input type="number" value={card.lounge_access?.international_per_year ?? ''} onChange={(e) => set({ lounge_access: { ...(card.lounge_access || {}), international_per_year: e.target.value === '' ? null : Number(e.target.value) } })} className="mt-1 h-9 font-mono-num" data-testid="admin-card-form-lounge-international" />
                  </div>
                  <div>
                    <Label className="text-xs">Lounge notes</Label>
                    <Input value={card.lounge_access?.notes ?? ''} onChange={(e) => set({ lounge_access: { ...(card.lounge_access || {}), notes: e.target.value } })} className="mt-1 h-9" data-testid="admin-card-form-lounge-notes" />
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="image" className="mt-4">
              {id ? (
                <CardAssetPanel
                  cardId={id}
                  bank={card.bank}
                  name={card.name}
                  image={card.image}
                  onChange={(newImage) => set({ image: newImage })}
                />
              ) : (
                <div className="rounded-lg border border-dashed border-border bg-[hsl(var(--surface-2))] p-6 text-sm text-muted-foreground italic" data-testid="card-asset-panel-disabled">
                  Save the card first — then you can upload + process an image here.
                </div>
              )}
            </TabsContent>

            <TabsContent value="transfer" className="mt-4 space-y-3">
              <div className="text-[11px] text-muted-foreground">
                Transfer partners are picked from the centralized <Link to="/admin/audit-log" className="underline hover:text-foreground">transfer-partner library</Link> instead of free-text entry. Use ratio "1:1", "2:1", etc.
              </div>
              {(card.transfer_partners || []).map((tp, idx) => (
                <div key={idx} className="grid grid-cols-2 sm:grid-cols-[1fr_90px_100px_70px_36px] gap-2 items-end">
                  <div className="col-span-2 sm:col-span-1">
                    <EntityPicker
                      value={tp.partner}
                      onChange={(slug, entity) => set({
                        transfer_partners: card.transfer_partners.map((x, i) => i === idx ? {
                          ...x,
                          partner: entity?.name || slug,
                          partner_slug: slug,
                          type: entity?.partner_type || x.type,
                        } : x),
                      })}
                      loader={({ q }) => AdminAPIExt.entities.transferPartners({ q })}
                      placeholder="Pick airline/hotel program…"
                      testid={`admin-card-tp-picker-${idx}`}
                    />
                  </div>
                  <Input value={tp.ratio} onChange={(e) => set({ transfer_partners: card.transfer_partners.map((x, i) => i === idx ? { ...x, ratio: e.target.value } : x) })} placeholder="1:1" className="h-9 font-mono-num" data-testid={`admin-card-tp-ratio-${idx}`} />
                  <Select value={tp.type} onValueChange={(v) => set({ transfer_partners: card.transfer_partners.map((x, i) => i === idx ? { ...x, type: v } : x) })}>
                    <SelectTrigger className="h-9" data-testid={`admin-card-tp-type-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="airline">Airline</SelectItem>
                      <SelectItem value="hotel">Hotel</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="number" value={tp.bonus_pct ?? 0} onChange={(e) => set({ transfer_partners: card.transfer_partners.map((x, i) => i === idx ? { ...x, bonus_pct: Number(e.target.value) || 0 } : x) })} placeholder="0%" className="h-9 font-mono-num" data-testid={`admin-card-tp-bonus-${idx}`} />
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-rose-500" onClick={() => set({ transfer_partners: card.transfer_partners.filter((_, i) => i !== idx) })} data-testid={`admin-card-tp-remove-${idx}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => set({ transfer_partners: [...(card.transfer_partners || []), { partner: '', partner_slug: null, ratio: '1:1', type: 'airline', bonus_pct: 0 }] })} data-testid="admin-card-tp-add">
                + Add partner
              </Button>
            </TabsContent>
          </Tabs>
        </Card>

        {/* RIGHT: inspector */}
        <div className="space-y-3 lg:sticky lg:top-16 self-start">
          <Card className="p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Live preview</div>
            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
              <CreditCardVisual card={card} size="md" />
            </motion.div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
              <div>
                <div className="text-muted-foreground">Annual fee</div>
                <div className="font-mono-num font-medium">{formatINR(Number(card.annual_fee) || 0)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Point value</div>
                <div className="font-mono-num font-medium">₹{Number(card.point_value_inr || 1).toFixed(2)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Rules</div>
                <div className="font-mono-num font-medium">{(card.rules || []).length}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Milestones</div>
                <div className="font-mono-num font-medium">{(card.milestones || []).length}</div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Validation</div>
            {validations.length === 0 ? (
              <div className="text-xs text-emerald-600" data-testid="admin-edit-validation-summary">All required fields look good.</div>
            ) : (
              <ul className="space-y-1 text-xs text-amber-600" data-testid="admin-edit-validation-summary">
                {validations.map((v) => (<li key={v}>• {v}</li>))}
              </ul>
            )}
          </Card>

          {!isNew && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Recent changes</div>
                <Button asChild size="sm" variant="ghost" className="h-6 px-2 text-[11px] gap-1 text-muted-foreground hover:text-foreground"><Link to="/admin/audit-log"><History className="h-3 w-3" /> All</Link></Button>
              </div>
              {audits.length === 0 ? (
                <div className="text-xs text-muted-foreground">No recent edits to this card.</div>
              ) : (
                <div className="space-y-1.5">
                  {audits.map((e) => (
                    <div key={e.id} className="text-[11px] flex items-center gap-2">
                      <Badge variant="outline" className="text-[9px] uppercase tracking-wide px-1">{e.action}</Badge>
                      <span className="truncate flex-1">{e.diff_summary}</span>
                      <span className="text-muted-foreground shrink-0" title={formatDateTime(e.timestamp)}>{formatDistanceToNow(e.timestamp)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive “{card.name}”?</AlertDialogTitle>
            <AlertDialogDescription>The card will be marked inactive but kept for historic transactions. You can reactivate it any time.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="admin-card-delete-confirm">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
